import { useState } from 'react';
import { API_ENDPOINTS, fetchWithRetry, getApiUrl } from '../config/api';
import useStore from '../store/useStore';
import {
  extractGameTokenFromLoginResponse,
  extractServerNowFromLoginResponse,
  extractTodayEndFromLoginResponse
} from '../utils/authResponse';

const LoginModal = () => {
  const { loginModalOpen, setLoginModalOpen, setUser, setApiLoading } = useStore();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (!loading) {
      setLoginModalOpen(false);
      setError('');
      setFormData({ email: '', password: '' });
      setLoading(false);
    }
  };

  class CryptoUtils {
    static async hashPassword(password) {
      try {
        if (window.crypto && window.crypto.subtle && (window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
          const encoder = new TextEncoder();
          const data = encoder.encode(password);
          const hashBuffer = await crypto.subtle.digest('SHA-256', data);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        } else {
          console.warn('Web Crypto API not available, using fallback hash');
          return this.simpleHash(password);
        }
      } catch (error) {
        console.error('Hash error:', error);
        return this.simpleHash(password);
      }
    }

    static simpleHash(str) {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return Math.abs(hash).toString(16).padStart(8, '0');
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setApiLoading(true, 'Logging in...');

    try {
      const hashPassword = await CryptoUtils.hashPassword(formData.password);

      const formData_req = new FormData();
      formData_req.append('Username', formData.email);
      formData_req.append('Password', hashPassword);
      formData_req.append('LoginOrigin', '6');
      formData_req.append('TokenOption', '1');

      const email_response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.REGISTER_ACCOUNT), {
        method: 'POST',
        headers: {},
        body: formData_req,
      });

      if (!email_response.ok) {
        const errorData = await email_response.json().catch(() => null);
        tgaLogEvent('payment_page', { type: 'email_login_error', api: API_ENDPOINTS.REGISTER_ACCOUNT, msg: toString(errorData) });
        throw new Error(errorData?.message || `Login failed with status ${email_response.status}`);
      }

      const platformUserData = await email_response.json();
      const platformToken = platformUserData.AccessToken;

      tgaLogEvent('payment_page', { type: 'email_login', api: API_ENDPOINTS.REGISTER_ACCOUNT, msg: JSON.stringify(platformUserData) });

      const loginRequest = {
        Fpid: String(platformUserData.Fpid),
        Sign: platformToken,
        Loginorigin: 6,
        TokenOption: 1
      };

      window['tga'].login(platformUserData.Fpid);

      const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.LOGIN_A10), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginRequest),
      });

      if (!response.ok) {
        tgaLogEvent('payment_page', { type: 'email_login_error', api: API_ENDPOINTS.LOGIN_A10, msg: toString(response.status) });
        throw new Error(`Backend returned ${response.status}`);
      }

      const userData = await response.json();
      const todayEnd = extractTodayEndFromLoginResponse(userData);
      const serverNow = extractServerNowFromLoginResponse(userData);
      const serverTimeOffsetMs = serverNow !== null ? Date.now() - (serverNow * 1000) : null;
      const loginGameToken = extractGameTokenFromLoginResponse(userData);
      console.log('[countdown-debug][LoginModal][email-login]', {
        todayEnd,
        serverNow,
        rawEndOfDay: userData?.EndOfDay,
        rawNow: userData?.Now,
      });

      if (userData && userData.Fpid) {
        const newUser = {
          id: String(userData.Fpid),
          playerid: userData.Player.ID,
          email: formData.email,
          name: userData.Player?.Name || formData.email.split('@')[0],
          Avatar: userData.Player?.Avatar || '',
          token: platformToken,
          platformToken,
          gameToken: loginGameToken,
          provider: 'email',
          Coin: userData.Player?.Coin || 0,
          Level: userData.Player?.Level || 0,
          VipLevel: userData.Player?.VipLevel || 0,
          Gem: userData.Player?.Gem || 0,
          ...(todayEnd !== null ? { todayEnd, today_end: todayEnd } : {}),
          ...(serverNow !== null ? { serverNow, serverTimeOffsetMs } : {})
        };
        setUser(newUser);

        tgaLogEvent('payment_page', { type: 'email_login_error', api: API_ENDPOINTS.LOGIN_A10, msg: JSON.stringify(newUser) });

        localStorage.setItem('emailCredentials', JSON.stringify({
          email: formData.email,
          password: formData.password
        }));

        handleClose();
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (error) {
      console.error('Email login error:', error);
      setError(error.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
      setApiLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    if (provider !== 'facebook') {
      setError('Only Facebook login is supported');
      return;
    }

    setLoading(true);
    setError('');
    setApiLoading(true, 'Connecting to Facebook...');

    try {
      const fbLoginResponse = await new Promise((resolve, reject) => {
        if (!window.FB) {
          reject(new Error('Facebook SDK not loaded'));
          return;
        }

        FB.login(
          function (response) {
            if (response.authResponse) {
              tgaLogEvent('payment_page', { type: 'fb_login', msg: JSON.stringify(response) });
              resolve(response.authResponse);
            } else {
              tgaLogEvent('payment_page', { type: 'fb_login_error', msg: toString(response) });
              reject(new Error('Facebook login cancelled or failed'));
            }
          },
          { scope: '', auth_type: 'rerequest' }
        );
      });

      const loginRequest = {
        appId: '118100871695288',
        openId: fbLoginResponse.userID,
        accessToken: fbLoginResponse.accessToken,
        signedRequest: fbLoginResponse.signedRequest || '',
        tokenOption: 1,
      };

      const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.LOGIN_FACEBOOK), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginRequest),
      });

      if (!response.ok) {
        tgaLogEvent('payment_page', { type: 'fb_login_error', api: API_ENDPOINTS.LOGIN_FACEBOOK, msg: toString(response) });
        throw new Error(`Backend returned ${response.status}`);
      }

      const fbUserData = await response.json();
      const facebookToken = extractGameTokenFromLoginResponse(fbUserData);
      const todayEnd = extractTodayEndFromLoginResponse(fbUserData);
      const serverNow = extractServerNowFromLoginResponse(fbUserData);
      const serverTimeOffsetMs = serverNow !== null ? Date.now() - (serverNow * 1000) : null;

      console.log('[auth-debug][facebook-login][LoginModal][backend-response]', fbUserData);
      console.log('[auth-debug][facebook-login][LoginModal][token-candidates]', {
        Token: fbUserData?.Token,
        GameToken: fbUserData?.GameToken,
        Gametoken: fbUserData?.Gametoken,
        gameToken: fbUserData?.gameToken,
      });

      if (fbUserData && fbUserData.Fpid && facebookToken) {
        window['tga'].login(fbUserData.Fpid);

        console.log('[countdown-debug][LoginModal][facebook-login]', {
          rawFacebookToken: !!facebookToken,
          todayEnd,
          serverNow,
          rawEndOfDay: fbUserData?.EndOfDay,
          rawNow: fbUserData?.Now,
        });

        let jsonUserData = {
          id: String(fbUserData.Fpid),
          playerid: fbUserData.Player?.ID || fbUserData.PlayerID,
          name: fbUserData.Player?.Name || 'Facebook User',
          Avatar: fbUserData.Player?.Avatar || '',
          token: facebookToken,
          gameToken: facebookToken,
          provider: 'facebook',
          Coin: fbUserData.Player?.Coin || 0,
          Level: fbUserData.Player?.Level || 0,
          VipLevel: fbUserData.Player?.VipLevel || 0,
          Gem: fbUserData.Player?.Gem || 0,
          ...(todayEnd !== null ? { todayEnd, today_end: todayEnd } : {}),
          ...(serverNow !== null ? { serverNow, serverTimeOffsetMs } : {})
        };
        console.log('[auth-debug][facebook-login][LoginModal][user-session]', {
          id: jsonUserData.id,
          playerid: jsonUserData.playerid,
          hasFacebookToken: !!jsonUserData.token,
          hasGameToken: !!jsonUserData.gameToken,
        });
        setUser(jsonUserData);
        tgaLogEvent('payment_page', { type: 'fb_login', api: API_ENDPOINTS.LOGIN_FACEBOOK, msg: JSON.stringify(jsonUserData) });
        handleClose();
      } else {
        throw new Error('Invalid response from backend');
      }

    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
      setApiLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const formLabelClass = 'mb-2 inline-block text-[0.92rem] font-semibold tracking-[0.02em] text-[#5b6484] dark:text-white/72';

  if (!loginModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(17,13,42,0.34)] px-4 backdrop-blur-[5px] dark:bg-[rgba(4,2,12,0.58)]">
      <section className="w-full max-w-[38rem] overflow-hidden rounded-[2rem] border border-[#dfe7ff] bg-[linear-gradient(160deg,rgba(255,255,255,0.96),rgba(244,242,255,0.92))] shadow-[0_34px_70px_rgba(87,108,180,0.22)] dark:border-white/10 dark:bg-[linear-gradient(160deg,rgba(27,14,53,0.92),rgba(10,5,22,0.96))] dark:shadow-[0_34px_70px_rgba(0,0,0,0.45)]">
        <div className="p-5 sm:p-6">
          <div className="text-center">
            <div className="mx-auto h-[2px] w-16 rounded-full bg-[linear-gradient(90deg,rgba(255,199,74,0),rgba(255,199,74,0.95),rgba(255,199,74,0))]" />
            <h2 className="mx-auto mt-4 max-w-[10ch] font-display text-[2.15rem] font-black leading-[0.92] tracking-[-0.04em] text-[#20163b] dark:text-white sm:text-[2.95rem]">
              Login to Fantasy Slots
            </h2>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-ruby/30 bg-ruby/10 px-4 py-3 text-sm text-ruby">
              {error}
            </div>
          )}

          {/* Warning card */}
          <div className="mt-5 rounded-[1.5rem] border border-[#ffd76e] bg-[linear-gradient(180deg,rgba(255,247,216,0.95),rgba(255,236,183,0.72))] p-[1px] shadow-[0_16px_30px_rgba(255,188,50,0.10)] dark:border-[#8b6a1d] dark:bg-[linear-gradient(180deg,rgba(255,215,110,0.18),rgba(255,215,110,0.1))]">
            <div className="rounded-[calc(1.5rem-1px)] bg-[linear-gradient(180deg,rgba(255,252,242,0.96),rgba(255,247,219,0.86))] px-4 py-4 dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,215,110,0.04))] sm:px-5">
              <div className="flex items-start gap-3 text-left">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#ffd76e] bg-[linear-gradient(180deg,#fff4c7,#ffd45f)] font-display text-xl font-black text-[#a45d00] shadow-[0_8px_18px_rgba(255,188,50,0.18)] dark:border-[#8b6a1d] dark:bg-[linear-gradient(180deg,rgba(255,215,110,0.28),rgba(255,180,57,0.14))] dark:text-goldSoft">
                  !
                </div>
                <div className="min-w-0">
                  <p className="font-display text-[1.1rem] font-black leading-[1.05] text-[#f57b00] sm:text-[1.35rem]">
                    Same Login Method Required
                  </p>
                  <p className="mt-2 text-[14px] leading-6 text-[#6f7487] dark:text-white/70 sm:text-[15px]">
                    Please log in using the same method as your game account to
                    ensure correct delivery.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Facebook login button */}
          <button
            type="button"
            onClick={() => handleSocialLogin('facebook')}
            disabled={loading}
            className="mt-6 w-full rounded-[1.2rem] border border-[#5f80ff]/30 bg-[linear-gradient(135deg,#2e67ff,#1d46bf)] px-5 py-4 font-display text-lg font-black uppercase tracking-[0.08em] text-white shadow-[0_18px_35px_rgba(54,125,255,0.18)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 sm:text-xl"
          >
            {loading ? 'Logging in...' : 'Continue With Facebook'}
          </button>

          <p className="mt-5 text-center text-base text-[#6f7487] dark:text-white/62 sm:text-lg">
            Or use email
          </p>

          {/* Email/Password form */}
          <form onSubmit={handleSubmit} className="mt-5 space-y-3.5">
            <label className="block">
              <span className={formLabelClass}>
                Email Address *
              </span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="player@example.com"
                required
                className="w-full rounded-[1.2rem] border border-[#dfe6ff] bg-white/88 px-4 py-3.5 text-base text-[#1d1537] outline-none transition placeholder:text-[#a8b1c8] focus:border-[#6d8dff] focus:ring-2 focus:ring-[#6d8dff]/20 dark:border-white/10 dark:bg-black/25 dark:text-white dark:placeholder:text-white/25 dark:focus:border-gold/60 dark:focus:ring-gold/20"
              />
            </label>

            <label className="block">
              <span className={formLabelClass}>
                Password *
              </span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                className="w-full rounded-[1.2rem] border border-[#dfe6ff] bg-white/88 px-4 py-3.5 text-base text-[#1d1537] outline-none transition placeholder:text-[#a8b1c8] focus:border-[#6d8dff] focus:ring-2 focus:ring-[#6d8dff]/20 dark:border-white/10 dark:bg-black/25 dark:text-white dark:placeholder:text-white/25 dark:focus:border-gold/60 dark:focus:ring-gold/20"
              />
            </label>

            <button
              type="submit"
              disabled={loading || !formData.email.trim() || !formData.password.trim()}
              className="mt-6 w-full rounded-full border border-emerald/60 bg-[linear-gradient(135deg,#27f7a6,#0d9d67)] px-5 py-4 font-display text-lg font-black uppercase tracking-[0.18em] text-[#03160f] shadow-emerald transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 sm:text-xl"
            >
              Login With Email
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default LoginModal;
